// src/services/PushNotificationService.js - Servicio modificado para solo tokens activos
import { db } from '../../../firebase';

class PushNotificationService {
  constructor() {
    this.EXPO_PUSH_API_URL = 'https://automation.maguitech.com/webhook/ampa-notifications';
    this.MAX_CONCURRENT_CALLS = 10;
    this.DELAY_BETWEEN_CALLS = 100;
  }

  /**
   * Validar si un token de Expo es válido
   */
  isValidExpoToken(token) {
    return token && (
      token.startsWith('ExponentPushToken[') || 
      token.startsWith('ExpoPushToken[') ||
      token.match(/^[a-z\d]{8}-[a-z\d]{4}-[a-z\d]{4}-[a-z\d]{4}-[a-z\d]{12}$/i)
    );
  }

  // Agrega esta función a tu PushNotificationService.js

/**
 * Calcular distribución por plataforma de tokens
 * @param {Array} tokens - Array de tokens con información de plataforma
 * @returns {Object} Distribución por plataforma
 */
calculatePlatformBreakdown(tokens) {
  try {
    console.log('📊 Calculando distribución por plataforma...');
    
    if (!tokens || !Array.isArray(tokens)) {
      console.warn('⚠️ Tokens no válidos para calcular distribución');
      return { ios: 0, android: 0, web: 0, other: 0 };
    }

    const breakdown = {
      ios: 0,
      android: 0,
      web: 0,
      other: 0
    };

    tokens.forEach(tokenData => {
      const platform = tokenData.platform?.toLowerCase() || 'unknown';
      
      switch (platform) {
        case 'ios':
          breakdown.ios++;
          break;
        case 'android':
          breakdown.android++;
          break;
        case 'web':
          breakdown.web++;
          break;
        default:
          breakdown.other++;
          break;
      }
    });

    console.log('✅ Distribución calculada:', breakdown);
    return breakdown;
  } catch (error) {
    console.error('❌ Error calculando distribución por plataforma:', error);
    return { ios: 0, android: 0, web: 0, other: 0 };
  }
}

/**
 * Enviar una notificación de prueba
 * @param {Object} notificationData - Datos de la notificación de prueba
 * @returns {Object} Resultado del envío de prueba
 */
async sendTestNotification(notificationData) {
  try {
    console.log('🧪 Enviando notificación de prueba...');
    
    // Para pruebas, podemos usar un token específico o uno aleatorio de los activos
    const activeTokens = await this.getAllActiveTokens(db);
    
    if (activeTokens.length === 0) {
      throw new Error('No hay tokens activos disponibles para enviar notificación de prueba');
    }

    // Tomar el primer token activo para la prueba
    const testToken = activeTokens[0];
    
    const testMessage = this.prepareExpoMessage(notificationData, testToken.token, testToken.userData);
    
    console.log('📤 Enviando mensaje de prueba a:', testToken.platform);
    
    const result = await this.sendSingleNotification(testMessage, testToken.token);
    
    return {
      success: true,
      totalTokens: 1,
      sentNotifications: result.success ? 1 : 0,
      failedNotifications: result.success ? 0 : 1,
      platformBreakdown: this.calculatePlatformBreakdown([testToken]),
      testToken: testToken.token.substring(0, 20) + '...',
      testPlatform: testToken.platform,
      result: result
    };
  } catch (error) {
    console.error('❌ Error enviando notificación de prueba:', error);
    throw error;
  }
}

/**
 * Enviar una sola notificación
 * @param {Object} message - Mensaje preparado para Expo
 * @param {string} token - Token del dispositivo
 * @returns {Object} Resultado del envío
 */
async sendSingleNotification(message, token) {
  try {
    console.log('📤 Enviando notificación individual...');
    
    const response = await fetch(this.EXPO_PUSH_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages: [message],
        tokens: [token]
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('✅ Respuesta del envío individual:', result);

    const isSuccess = response.ok && (!result.status || result.status !== 'error');
    return {
      success: isSuccess,
      ticketId: result.ticketId || 'unknown',
      status: result.status || 'sent',
      token: token,
      ...result
    };
  } catch (error) {
    console.error('❌ Error enviando notificación individual:', error);
    return {
      success: false,
      error: error.message,
      token: token
    };
  }
}

/**
 * Enviar múltiples notificaciones de forma secuencial
 * @param {Array} messages - Array de mensajes
 * @param {Array} tokens - Array de tokens
 * @param {Function} progressCallback - Callback de progreso
 * @returns {Object} Resultado del envío múltiple
 */
async sendMultipleNotifications(messages, tokens, progressCallback = null) {
  try {
    console.log(`📤 Enviando ${messages.length} notificaciones de forma secuencial...`);
    
    const successful = [];
    const failed = [];
    const invalidTokens = [];
    const errors = [];

    for (let i = 0; i < messages.length; i++) {
      try {
        // Callback de progreso
        if (progressCallback) {
          const progress = Math.round((i / messages.length) * 100);
          progressCallback(progress);
        }

        const result = await this.sendSingleNotification(messages[i], tokens[i]);
        
        if (result.success) {
          successful.push(result);
        } else {
          failed.push(result);
          if (result.error && result.error.includes('invalid')) {
            invalidTokens.push(tokens[i]);
          }
        }

        // Pequeña pausa entre envíos para evitar rate limiting
        if (i < messages.length - 1) {
          await new Promise(resolve => setTimeout(resolve, this.DELAY_BETWEEN_CALLS));
        }
      } catch (error) {
        console.error(`❌ Error enviando mensaje ${i}:`, error);
        failed.push({ token: tokens[i], error: error.message });
        errors.push(error.message);
      }
    }

    // Callback final
    if (progressCallback) {
      progressCallback(100);
    }

    console.log(`✅ Envío secuencial completado: ${successful.length} exitosos, ${failed.length} fallidos`);
    
    return {
      successful,
      failed,
      invalidTokens,
      errors
    };
  } catch (error) {
    console.error('❌ Error en envío múltiple secuencial:', error);
    throw error;
  }
}

/**
 * Enviar múltiples notificaciones de forma concurrente
 * @param {Array} messages - Array de mensajes
 * @param {Array} tokens - Array de tokens
 * @param {Function} progressCallback - Callback de progreso
 * @returns {Object} Resultado del envío múltiple concurrente
 */
async sendMultipleNotificationsConcurrent(messages, tokens, progressCallback = null) {
  try {
    console.log(`📤 Enviando ${messages.length} notificaciones de forma concurrente...`);
    
    const successful = [];
    const failed = [];
    const invalidTokens = [];
    const errors = [];

    // Dividir en lotes para evitar sobrecargar el servidor
    const batchSize = this.MAX_CONCURRENT_CALLS;
    const batches = [];
    
    for (let i = 0; i < messages.length; i += batchSize) {
      batches.push({
        messages: messages.slice(i, i + batchSize),
        tokens: tokens.slice(i, i + batchSize)
      });
    }

    let completedBatches = 0;

    for (const batch of batches) {
      try {
        // Enviar lote de forma concurrente
        const batchPromises = batch.messages.map((message, index) => 
          this.sendSingleNotification(message, batch.tokens[index])
        );

        const batchResults = await Promise.allSettled(batchPromises);
        
        batchResults.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value.success) {
            successful.push(result.value);
          } else {
            const failedResult = {
              token: batch.tokens[index],
              error: result.reason?.message || result.value?.error || 'Unknown error'
            };
            failed.push(failedResult);
            
            if (failedResult.error.includes('invalid')) {
              invalidTokens.push(batch.tokens[index]);
            }
            errors.push(failedResult.error);
          }
        });

        completedBatches++;
        
        // Callback de progreso
        if (progressCallback) {
          const progress = Math.round((completedBatches / batches.length) * 100);
          progressCallback(progress);
        }

        // Pausa entre lotes
        if (completedBatches < batches.length) {
          await new Promise(resolve => setTimeout(resolve, this.DELAY_BETWEEN_CALLS * 2));
        }
      } catch (error) {
        console.error('❌ Error procesando lote concurrente:', error);
        errors.push(error.message);
      }
    }

    console.log(`✅ Envío concurrente completado: ${successful.length} exitosos, ${failed.length} fallidos`);
    
    return {
      successful,
      failed,
      invalidTokens,
      errors
    };
  } catch (error) {
    console.error('❌ Error en envío múltiple concurrente:', error);
    throw error;
  }
}

  /**
   * Verificar si un token está activo
   * @param {Object} tokenObj - Objeto del token desde Firestore
   * @returns {boolean}
   */
  isTokenActive(tokenObj) {
    // El token debe estar explícitamente marcado como activo
    if (tokenObj.active === false) {
      return false;
    }

    // Por defecto, si no hay campo 'active', consideramos que está activo
    // pero podemos agregar más validaciones aquí
    return true;
  }

  /**
   * Verificar si un token es reciente (no más de 30 días sin actualizar)
   * @param {Object} tokenObj - Objeto del token desde Firestore
   * @returns {boolean}
   */
  isTokenRecent(tokenObj) {
    if (!tokenObj.lastUpdated) {
      return false; // Si no tiene lastUpdated, no es reciente
    }

    try {
      const lastUpdated = tokenObj.lastUpdated.toDate ? 
        tokenObj.lastUpdated.toDate() : 
        new Date(tokenObj.lastUpdated);
      
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      return lastUpdated > thirtyDaysAgo;
    } catch (error) {
      console.error('Error verificando fecha del token:', error);
      return false;
    }
  }

  /**
   * Obtener tokens de usuarios específicos desde Firestore - SOLO ACTIVOS
   */
  async getTokensForUsers(db, userIds, platform = null) {
    try {
      console.log(`🔍 Obteniendo tokens ACTIVOS para ${userIds.length} usuarios...`);
      
      const { doc, getDoc } = await import('firebase/firestore');
      const tokens = [];
      let totalTokensFound = 0;
      let activeTokensFound = 0;

      for (const userId of userIds) {
        try {
          const userDocRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            if (userData.expoTokens && Array.isArray(userData.expoTokens)) {
              userData.expoTokens.forEach((tokenObj) => {
                totalTokensFound++;
                
                // FILTROS PARA TOKENS ACTIVOS:
                
                // 1. Verificar que el token es válido
                if (!this.isValidExpoToken(tokenObj.token)) {
                  console.log(`❌ Token inválido ignorado para usuario ${userId}`);
                  return;
                }
                
                // 2. Verificar que está marcado como activo
                if (!this.isTokenActive(tokenObj)) {
                  console.log(`⚠️ Token inactivo ignorado para usuario ${userId}`);
                  return;
                }

                // 3. Verificar que es reciente (opcional, descomenta si quieres esta validación)
                // if (!this.isTokenRecent(tokenObj)) {
                //   console.log(`⏰ Token obsoleto ignorado para usuario ${userId}`);
                //   return;
                // }

                // 4. Filtrar por plataforma si se especifica
                if (platform && tokenObj.deviceInfo && 
                    tokenObj.deviceInfo.platform?.toLowerCase() !== platform.toLowerCase()) {
                  console.log(`📱 Token de plataforma ${tokenObj.deviceInfo.platform} ignorado (buscando ${platform})`);
                  return;
                }
                
                // Si pasa todas las validaciones, agregar el token
                activeTokensFound++;
                tokens.push({
                  token: tokenObj.token,
                  userId: userId,
                  platform: tokenObj.deviceInfo?.platform || 'unknown',
                  deviceInfo: tokenObj.deviceInfo,
                  lastUpdated: tokenObj.lastUpdated,
                  active: tokenObj.active !== false, // Explícitamente true o undefined
                  userData: {
                    name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                    email: userData.email,
                    userId: userId
                  }
                });
              });
            }
          }
        } catch (error) {
          console.error(`❌ Error obteniendo tokens para usuario ${userId}:`, error);
        }
      }

      console.log(`✅ Se encontraron ${activeTokensFound} tokens ACTIVOS de ${totalTokensFound} tokens totales`);
      console.log(`📊 Tasa de tokens activos: ${Math.round((activeTokensFound / Math.max(totalTokensFound, 1)) * 100)}%`);
      
      return tokens;
    } catch (error) {
      console.error('❌ Error general obteniendo tokens:', error);
      return [];
    }
  }

  /**
   * Obtener todos los tokens activos desde Firestore - SOLO ACTIVOS
   */
  async getAllActiveTokens(db, platform = null) {
    try {
      console.log('🔍 Obteniendo TODOS los tokens ACTIVOS...');
      
      const { collection, getDocs, query } = await import('firebase/firestore');
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      const tokens = [];
      let totalUsers = 0;
      let usersWithTokens = 0;
      let totalTokensFound = 0;
      let activeTokensFound = 0;

      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        const userId = doc.id;
        totalUsers++;
        
        if (userData.expoTokens && Array.isArray(userData.expoTokens)) {
          usersWithTokens++;
          
          userData.expoTokens.forEach((tokenObj) => {
            totalTokensFound++;
            
            // FILTROS PARA TOKENS ACTIVOS:
            
            // 1. Verificar que el token es válido
            if (!this.isValidExpoToken(tokenObj.token)) {
              return;
            }
            
            // 2. Verificar que está marcado como activo
            if (!this.isTokenActive(tokenObj)) {
              return;
            }

            // 3. Verificar que es reciente (opcional)
            // if (!this.isTokenRecent(tokenObj)) {
            //   return;
            // }

            // 4. Filtrar por plataforma si se especifica
            if (platform && tokenObj.deviceInfo && 
                tokenObj.deviceInfo.platform?.toLowerCase() !== platform.toLowerCase()) {
              return;
            }
            
            // Si pasa todas las validaciones, agregar el token
            activeTokensFound++;
            tokens.push({
              token: tokenObj.token,
              userId: userId,
              platform: tokenObj.deviceInfo?.platform || 'unknown',
              deviceInfo: tokenObj.deviceInfo,
              lastUpdated: tokenObj.lastUpdated,
              active: tokenObj.active !== false,
              userData: {
                name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                email: userData.email,
                userId: userId
              }
            });
          });
        }
      });

      // Log estadísticas detalladas
      console.log(`📊 Estadísticas de tokens:`);
      console.log(`   👥 Usuarios totales: ${totalUsers}`);
      console.log(`   📱 Usuarios con tokens: ${usersWithTokens}`);
      console.log(`   🔍 Tokens totales encontrados: ${totalTokensFound}`);
      console.log(`   ✅ Tokens ACTIVOS: ${activeTokensFound}`);
      console.log(`   📈 Tasa de actividad: ${Math.round((activeTokensFound / Math.max(totalTokensFound, 1)) * 100)}%`);
      
      if (platform) {
        console.log(`   📱 Plataforma filtrada: ${platform}`);
      }

      return tokens;
    } catch (error) {
      console.error('❌ Error obteniendo todos los tokens activos:', error);
      return [];
    }
  }

  /**
   * Obtener tokens para usuarios con hijos en cursos específicos - SOLO ACTIVOS
   */
  async getTokensForCourseUsers(db, courses, platform = null) {
    try {
      if (!courses || courses.length === 0) {
        return [];
      }

      console.log(`🎓 Obteniendo tokens ACTIVOS para cursos: ${courses.join(', ')}`);

      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      // Obtener padres con hijos en los cursos especificados
      const childrenQuery = query(
        collection(db, 'children'),
        where('curso', 'in', courses)
      );
      const childrenSnapshot = await getDocs(childrenQuery);
      
      const parentIds = new Set();
      childrenSnapshot.forEach(doc => {
        const childData = doc.data();
        if (childData.parentId) {
          parentIds.add(childData.parentId);
        }
      });

      console.log(`👥 Encontrados ${parentIds.size} padres con hijos en los cursos especificados`);

      // Obtener solo tokens activos para estos padres
      return await this.getTokensForUsers(db, Array.from(parentIds), platform);
    } catch (error) {
      console.error('❌ Error obteniendo tokens por curso:', error);
      return [];
    }
  }

  /**
   * Obtener token de dispositivo específico - SOLO SI ESTÁ ACTIVO
   */
  async getSpecificDeviceToken(db, userId, deviceId) {
    try {
      console.log(`📱 Obteniendo token ACTIVO para dispositivo específico: ${deviceId}`);
      
      const { doc, getDoc } = await import('firebase/firestore');
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        console.log(`❌ Usuario ${userId} no encontrado`);
        return [];
      }

      const userData = userDoc.data();
      if (!userData.expoTokens || !Array.isArray(userData.expoTokens)) {
        console.log(`❌ Usuario ${userId} no tiene tokens`);
        return [];
      }

      const deviceIndex = parseInt(deviceId.split('_')[1]);
      const tokenObj = userData.expoTokens[deviceIndex];

      if (!tokenObj) {
        console.log(`❌ Token con índice ${deviceIndex} no encontrado`);
        return [];
      }

      // Verificar que el token específico esté activo
      if (!this.isValidExpoToken(tokenObj.token)) {
        console.log(`❌ Token específico no es válido`);
        return [];
      }

      if (!this.isTokenActive(tokenObj)) {
        console.log(`❌ Token específico no está activo`);
        return [];
      }

      console.log(`✅ Token específico está activo y es válido`);
      
      return [{
        token: tokenObj.token,
        userId: userId,
        platform: tokenObj.deviceInfo?.platform || 'unknown',
        deviceInfo: tokenObj.deviceInfo,
        lastUpdated: tokenObj.lastUpdated,
        active: tokenObj.active !== false,
        userData: {
          name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
          email: userData.email,
          userId: userId
        }
      }];
    } catch (error) {
      console.error('❌ Error obteniendo token de dispositivo específico:', error);
      return [];
    }
  }

  /**
   * Función de utilidad para limpiar tokens inactivos de un usuario
   * (Esta función se puede usar desde el portal de administración)
   */
  async cleanupInactiveTokensForUser(db, userId) {
    try {
      console.log(`🧹 Limpiando tokens inactivos para usuario: ${userId}`);
      
      const { doc, getDoc, updateDoc } = await import('firebase/firestore');
      const userDocRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        return { success: false, error: 'Usuario no encontrado' };
      }

      const userData = userDoc.data();
      if (!userData.expoTokens || !Array.isArray(userData.expoTokens)) {
        return { success: true, message: 'Usuario no tiene tokens', removed: 0, kept: 0 };
      }

      const originalCount = userData.expoTokens.length;
      
      // Filtrar solo tokens activos y válidos
      const activeTokens = userData.expoTokens.filter(tokenObj => {
        return this.isValidExpoToken(tokenObj.token) && 
               this.isTokenActive(tokenObj);
               // && this.isTokenRecent(tokenObj); // Descomenta si quieres filtrar por fecha
      });

      const removedCount = originalCount - activeTokens.length;

      if (removedCount > 0) {
        await updateDoc(userDocRef, {
          expoTokens: activeTokens,
          lastTokenCleanup: new Date()
        });
        
        console.log(`✅ Limpieza completada: ${removedCount} tokens removidos, ${activeTokens.length} mantenidos`);
      } else {
        console.log(`✅ No hay tokens para limpiar`);
      }

      return {
        success: true,
        removed: removedCount,
        kept: activeTokens.length,
        message: `Limpieza completada: ${removedCount} tokens removidos`
      };
    } catch (error) {
      console.error('❌ Error limpiando tokens:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Función de utilidad para limpiar tokens inactivos de todos los usuarios
   * (Esta función se puede usar desde el portal de administración)
   */
  async cleanupAllInactiveTokens(db) {
    try {
      console.log('🧹 Iniciando limpieza global de tokens inactivos...');
      
      const { collection, getDocs, query } = await import('firebase/firestore');
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      let totalUsers = 0;
      let usersProcessed = 0;
      let totalTokensRemoved = 0;
      let totalTokensKept = 0;

      for (const userDoc of usersSnapshot.docs) {
        totalUsers++;
        const userId = userDoc.id;
        
        try {
          const result = await this.cleanupInactiveTokensForUser(db, userId);
          if (result.success) {
            usersProcessed++;
            totalTokensRemoved += result.removed;
            totalTokensKept += result.kept;
          }
        } catch (error) {
          console.error(`❌ Error procesando usuario ${userId}:`, error);
        }
      }

      const summary = {
        success: true,
        totalUsers,
        usersProcessed,
        totalTokensRemoved,
        totalTokensKept,
        message: `Limpieza global completada: ${totalTokensRemoved} tokens removidos de ${usersProcessed} usuarios`
      };

      console.log(`✅ Limpieza global completada:`);
      console.log(`   👥 Usuarios procesados: ${usersProcessed}/${totalUsers}`);
      console.log(`   🗑️ Tokens removidos: ${totalTokensRemoved}`);
      console.log(`   ✅ Tokens mantenidos: ${totalTokensKept}`);

      return summary;
    } catch (error) {
      console.error('❌ Error en limpieza global:', error);
      return { success: false, error: error.message };
    }
  }

  // ... resto de métodos sin cambios (sendSingleNotification, etc.)
  
  /**
   * Preparar mensaje de notificación para Expo
   */
  prepareExpoMessage(notification, token, userData = {}) {
    const priority = notification.tipo === 'urgent' ? 'high' : 'normal';
    const sound = notification.tipo === 'urgent' ? 'default' : 'default';

    const data = {
      notificationId: notification.id || `temp_${Date.now()}`,
      tipo: notification.tipo,
      timestamp: new Date().toISOString(),
      userId: userData.userId || 'unknown',
      ...(notification.cursos && { cursos: notification.cursos }),
      ...(notification.enlaces && { enlaces: notification.enlaces }),
      ...(notification.actionUrl && { actionUrl: notification.actionUrl }),
      ...(notification.customData && { customData: notification.customData })
    };

    return {
      to: token,
      title: notification.título,
      body: notification.descripción,
      sound: sound,
      priority: priority,
      data: data,
      channelId: notification.channelId || 'default',
      badge: 1,
      _displayInForeground: true,
      ttl: 86400
    };
  }

  /**
   * Función principal para enviar notificaciones push (MODIFICADA)
   */
  async sendPushNotifications(notification, filters, db, progressCallback = null, useConcurrent = false) {
    try {
      console.log('🚀 Iniciando envío de notificaciones push a TOKENS ACTIVOS...');
      console.log('📝 Notificación:', notification);
      console.log('🎯 Filtros:', filters);
      console.log('⚡ Modo concurrente:', useConcurrent);

      // 1. Obtener tokens ACTIVOS según los filtros
      let tokenData = [];

      switch (filters.targetType) {
        case 'all':
          tokenData = await this.getAllActiveTokens(db, filters.platform);
          break;

        case 'course':
          tokenData = await this.getTokensForCourseUsers(db, filters.courses, filters.platform);
          break;

        case 'user':
          tokenData = await this.getTokensForUsers(db, [filters.specificUserId], filters.platform);
          break;

        case 'device':
          tokenData = await this.getSpecificDeviceToken(db, filters.specificUserId, filters.specificDeviceId);
          break;

        default:
          throw new Error('Tipo de filtro no válido');
      }

      if (tokenData.length === 0) {
        console.log('⚠️ No se encontraron tokens ACTIVOS para enviar');
        return {
          success: true,
          totalTokens: 0,
          sentNotifications: 0,
          failedNotifications: 0,
          invalidTokens: [],
          errors: [],
          platformBreakdown: { ios: 0, android: 0, web: 0, other: 0 },
          filters: filters,
          note: 'No hay tokens activos disponibles para los filtros especificados'
        };
      }

      console.log(`📱 Enviando notificaciones a ${tokenData.length} dispositivos ACTIVOS...`);

      // Log de breakdown de tokens activos
      const platformBreakdown = this.calculatePlatformBreakdown(tokenData);
      console.log(`📊 Distribución de tokens activos:`, platformBreakdown);

      // 2. Preparar mensajes para cada token
      const messages = tokenData.map(tokenInfo => 
        this.prepareExpoMessage(notification, tokenInfo.token, tokenInfo.userData)
      );

      const tokens = tokenData.map(tokenInfo => tokenInfo.token);

      // 3. Enviar usando el método seleccionado
      let sendResults;
      if (useConcurrent) {
        sendResults = await this.sendMultipleNotificationsConcurrent(messages, tokens, progressCallback);
      } else {
        sendResults = await this.sendMultipleNotifications(messages, tokens, progressCallback);
      }

      // 4. Procesar y formatear resultados finales
      const finalResults = {
        success: true,
        totalTokens: tokenData.length,
        sentNotifications: sendResults.successful.length,
        failedNotifications: sendResults.failed.length,
        invalidTokens: sendResults.invalidTokens,
        errors: sendResults.errors,
        tickets: sendResults.successful.map(result => ({
          token: result.token,
          ticket: result.ticketId,
          status: result.status
        })),
        platformBreakdown: platformBreakdown,
        filters: filters,
        sendMode: useConcurrent ? 'concurrent' : 'sequential',
        onlyActiveTokens: true, // Indicador de que solo se usaron tokens activos
        processingTime: Date.now()
      };

      // 5. Log de resumen
      console.log('✅ Envío completado (SOLO TOKENS ACTIVOS):');
      console.log(`   📊 Total tokens activos: ${finalResults.totalTokens}`);
      console.log(`   ✅ Exitosos: ${finalResults.sentNotifications}`);
      console.log(`   ❌ Fallidos: ${finalResults.failedNotifications}`);
      console.log(`   🗑️ Tokens inválidos: ${finalResults.invalidTokens.length}`);

      return finalResults;

    } catch (error) {
      console.error('❌ Error general enviando notificaciones push:', error);
      throw error;
    }
  }

  // ... resto de métodos (sendSingleNotification, sendMultipleNotifications, etc.)
}

// Exportar instancia singleton
const pushNotificationService = new PushNotificationService();
export default pushNotificationService;